import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { types as coreTypes } from '@sitebuilder/core';
import { themes as commonThemes, localization as commonLocalization } from '@sitebuilder/common';
import { ThemesApiClient } from '../api/themesApiClient';
import { ThunkResult } from './index';

export interface StateShape {
    theme: coreTypes.Theme | null;
    isThemeLoaded: boolean;
    themeLoading: boolean;
    themeLoadError: any | null;
    themeSaveLoading: boolean;
    themeSaveSucess: string;
    themeSaveError: any | null;
    themes: [coreTypes.Theme] | null;
    themesLoading: boolean;
    themesLoadError: any | null;
    selectedThemes: string[];
    versions: any[] | null;
    versionsLoading: boolean;
    versionsLoadError: any | null;
    revertedVersion: boolean;
    revertedVersionError: any | null;
    colorPalettes: coreTypes.Palettes | null;
    showDeletedItems: boolean;
    newFolderSaveLoading: boolean;
    newFolderSaveError: any | null;
    newFolderSaveSucess: string;
}

export const themesDefaultState: StateShape = {
    theme: null,
    isThemeLoaded: false,
    themeLoading: false,
    themeLoadError: null,
    themeSaveLoading: false,
    themeSaveSucess: '',
    themeSaveError: null,
    themes: null,
    themesLoading: false,
    themesLoadError: null,
    selectedThemes: [],
    versions: null,
    versionsLoading: false,
    versionsLoadError: null,
    revertedVersion: false,
    revertedVersionError: null,
    colorPalettes: null,
    showDeletedItems: false,
    newFolderSaveLoading: false,
    newFolderSaveError: null,
    newFolderSaveSucess: '',
};

const apiClient = new ThemesApiClient();

export const actionCreators = {
    loadTheme: (themeId: string): ThunkResult<Promise<coreTypes.Theme | undefined>> => {
        return async (dispatch, getState) => {
            const state = getState();
            const accessToken = state.auth.user?.access_token;
            dispatch(themesReducerActions.THEME_LOAD_START());

            try {
                const theme = await apiClient.getTheme(themeId, {
                    accessToken,
                });
                dispatch(themesReducerActions.THEME_LOAD_END({ theme }));

                if (theme) {
                    dispatch(themesReducerActions.SET_IS_THEME_LOADED(true));
                }

                return theme;
            } catch (err: any) {
                console.error(err);
                dispatch(themesReducerActions.SET_IS_THEME_LOADED(true));
                dispatch(themesReducerActions.THEME_LOAD_END({ error: { message: err.message } }));
            }
        };
    },
    saveNewTheme: (
        themeId: string,
        theme: coreTypes.Theme | {}
    ): ThunkResult<Promise<boolean | void>> => {
        return async (dispatch, getState) => {
            const state = getState();
            const locale = state?.templateEditor?.documentMetadata?.locale || 'en-US';
            const appI18n = commonLocalization.getLocalization(locale);

            const accessToken = state.auth.user?.access_token;
            dispatch(themesReducerActions.THEME_SAVE_START());

            try {
                await apiClient.saveTheme(themeId, theme, {
                    accessToken,
                });
                dispatch(
                    themesReducerActions.THEME_SAVE_END({
                        success: appI18n.t('theme_form_save_success'),
                    })
                );
                return true;
            } catch (err: any) {
                console.error(err);
                dispatch(
                    themesReducerActions.THEME_SAVE_END({
                        error: { message: err.message },
                    })
                );
                return false;
            }
        };
    },
    saveNewFolder: (folderName: string): ThunkResult<Promise<boolean | void>> => {
        return async (dispatch, getState) => {
            const state = getState();
            const locale = state?.templateEditor?.documentMetadata?.locale || 'en-US';
            const appI18n = commonLocalization.getLocalization(locale);

            const accessToken = state.auth.user?.access_token;
            dispatch(themesReducerActions.NEWFOLDER_SAVE_START());

            try {
                await apiClient.saveNewFolder(folderName, {
                    accessToken,
                });
                dispatch(
                    themesReducerActions.NEWFOLDER_SAVE_END({
                        success: appI18n.t('folder_form_save_success'),
                    })
                );
                return true;
            } catch (err: any) {
                console.error(err);
                dispatch(
                    themesReducerActions.NEWFOLDER_SAVE_END({
                        error: { message: err.message },
                    })
                );
                return false;
            }
        };
    },
    listThemes: (
        folderName: string,
        filters: { idPattern?: string[]; authorsIn?: Record<string, unknown> | [] } = {
            idPattern: [],
            authorsIn: [],
        },
        sort?: { field?: string; dir?: string },
        take?: number,
        skip?: number
    ): ThunkResult<Promise<boolean | void>> => {
        return async (dispatch, getState) => {
            const state = getState();
            const accessToken = state.auth.user?.access_token;
            dispatch(themesReducerActions.THEMES_LOAD_START());

            try {
                const themes = await apiClient.listThemes(
                    folderName,
                    state.themes.showDeletedItems,
                    {
                        accessToken,
                    },
                    filters,
                    sort,
                    // { field: '', dir: 'desc' },
                    take,
                    skip
                );
                dispatch(themesReducerActions.THEMES_LOAD_END({ themes }));
                return true;
            } catch (err: any) {
                console.error(err);
                dispatch(
                    themesReducerActions.THEMES_LOAD_END({
                        error: { message: err.message },
                    })
                );
                return false;
            }
        };
    },
    setSelectedThemes: (selectedThemes: string[]): ThunkResult<Promise<void>> => {
        return async (dispatch) => {
            dispatch(themesReducerActions.SET_SELECTED_THEMES(selectedThemes));
        };
    },
    unselectThemes: (selectedThemes: string[]): ThunkResult<Promise<void>> => {
        return async (dispatch) => {
            dispatch(themesReducerActions.REMOVE_SELECTED_THEMES(selectedThemes));
        };
    },
    unselectAllThemes: (): ThunkResult<Promise<void>> => {
        return async (dispatch) => {
            dispatch(themesReducerActions.REMOVE_ALL_SELECTED_THEMES());
        };
    },
    loadThemeVersions: (themeName: string): ThunkResult<Promise<void>> => {
        return async (dispatch, getState) => {
            const state = getState();
            const accessToken = state.auth.user?.access_token;
            dispatch(themesReducerActions.VERSIONS_LOAD_START());

            try {
                const items = await apiClient.getThemeVersions(themeName, {
                    accessToken,
                });
                dispatch(
                    themesReducerActions.VERSIONS_LOAD_END({
                        versions: items,
                    })
                );
            } catch (err: any) {
                dispatch(
                    themesReducerActions.VERSIONS_LOAD_END({
                        error: { message: err.message },
                    })
                );
            }
        };
    },

    revertVersions: (themeName: string | null, versionNumber: any) => {
        return async (dispatch: any, getState: any) => {
            const state = getState();
            const accessToken = state.auth.user?.access_token;

            try {
                const results = await apiClient.revertVersion(themeName, versionNumber, {
                    accessToken,
                });
                const revertedItem = await results.json();

                if (!isNaN(revertedItem.VersionNumber)) {
                    dispatch(themesReducerActions.REVERTED_VERSION({}));
                }
            } catch (err: any) {
                dispatch(
                    themesReducerActions.REVERTED_VERSION({
                        error: { message: err.message },
                    })
                );
            }
        };
    },

    loadDefaultColorPalettes: (): ThunkResult<Promise<void>> => {
        return async (dispatch) => {
            dispatch(themesReducerActions.LOAD_DEFAULT_COLOR_PALETTES());
        };
    },

    setShowDeletedItems: (): ThunkResult<Promise<void>> => {
        return async (dispatch, getState) => {
            const state = getState();
            dispatch(themesReducerActions.SET_SHOW_DELETED_ITEMS(!state.themes.showDeletedItems));
        };
    },
};

const themesSlice = createSlice({
    name: 'themes',
    initialState: themesDefaultState,
    reducers: {
        THEME_LOAD_START(draft) {
            draft.themeLoading = true;
            draft.theme = null;
            draft.themeLoadError = null;
            draft.themeSaveSucess = '';
            draft.newFolderSaveSucess = '';
        },
        THEME_LOAD_END(draft, action: PayloadAction<{ theme?: coreTypes.Theme; error?: any }>) {
            const { theme, error } = action.payload;
            draft.themeLoading = false;
            draft.theme = theme || null;
            draft.colorPalettes = Object.assign(
                {},
                commonThemes.defaultPalettes,
                theme?.defaultPalettes || {},
                theme?.palettes || {}
            );
            draft.themeLoadError = error;
        },
        THEME_SAVE_START(draft) {
            draft.themeSaveLoading = true;
            draft.themeSaveError = null;
        },
        THEME_SAVE_END(draft, action: PayloadAction<{ success?: string; error?: any }>) {
            const { success, error } = action.payload;
            draft.themeSaveLoading = false;
            draft.themeSaveSucess = success || '';
            draft.themeSaveError = error;
        },
        THEMES_LOAD_START(draft) {
            draft.themesLoading = true;
            draft.themes = null;
            draft.themesLoadError = null;
        },
        THEMES_LOAD_END(draft, action: PayloadAction<{ themes?: [coreTypes.Theme]; error?: any }>) {
            const { themes, error } = action.payload;
            draft.themesLoading = false;
            draft.themes = themes || null;
            draft.themesLoadError = error;
            draft.revertedVersion = false;
        },
        SET_IS_THEME_LOADED(draft, action: PayloadAction<boolean>) {
            draft.isThemeLoaded = action.payload;
        },
        SET_SELECTED_THEMES(draft, action: PayloadAction<string[]>) {
            draft.selectedThemes = [...draft.selectedThemes, ...action.payload];
        },
        REMOVE_SELECTED_THEMES(draft, action: PayloadAction<string[]>) {
            draft.selectedThemes = draft.selectedThemes.filter(
                (theme) => !action.payload.includes(theme)
            );
        },
        REMOVE_ALL_SELECTED_THEMES(draft) {
            draft.selectedThemes = [];
        },
        VERSIONS_LOAD_START(draft) {
            draft.versionsLoading = true;
            draft.versions = null;
            draft.versionsLoadError = null;
        },
        VERSIONS_LOAD_END(draft, action: PayloadAction<{ versions?: any[]; error?: any }>) {
            const { versions, error } = action.payload;
            draft.versionsLoading = false;
            draft.versions = versions || null;
            draft.versionsLoadError = error;
            draft.revertedVersion = false;
        },
        NEWFOLDER_SAVE_START(draft) {
            draft.newFolderSaveLoading = true;
            draft.newFolderSaveError = null;
        },
        NEWFOLDER_SAVE_END(draft, action: PayloadAction<{ success?: string; error?: any }>) {
            const { success, error } = action.payload;
            draft.newFolderSaveLoading = false;
            draft.newFolderSaveSucess = success || '';
            draft.newFolderSaveError = error;
        },
        REVERTED_VERSION(draft, action: PayloadAction<{ error?: any }>) {
            draft.revertedVersion = true;
            draft.revertedVersionError = action.payload.error;
        },
        LOAD_DEFAULT_COLOR_PALETTES(draft) {
            draft.colorPalettes = commonThemes.defaultPalettes;
        },
        SET_SHOW_DELETED_ITEMS(draft, action: PayloadAction<boolean>) {
            draft.showDeletedItems = action.payload;
        },
    },
});

export const themesReducerActions: any = themesSlice.actions;
export const themesReducer = themesSlice.reducer;
